import React, { useState, useCallback, useRef } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useNavigate } from 'react-router-dom';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions,
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import UnitDialog from '../products/ProductDialog';

interface OnHand {
  id: string;
  unitId: string;
  storageUnitId: string;
  warehouseId: string;
  boxRefNumber: string;
  quantity: number;
  unit: UnitProps;
  storageUnit: StorageUnitProps;
  warehouse: WarehouseProps;
  createdAt: string;
  updatedAt: string;
}

interface UnitProps {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  symbol: string;
  unitOfMeasurement: string;
  isSynced: boolean;
  createdAt: string;
  updatedAt: string;
  onHandQuantity: number;
  expectedQuantity: number;
  itemAdditionalProperties: Record<string, any>;
  itemMinNumber?: number | null;
  itemMaxNumber?: number | null;
}

interface StorageUnitProps {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentWarehouseId: string;
  parentStorageUnitId: string | null;
  parentWarehouseName: string;
  parentStorageUnitName: string | null;
  isLeaf: boolean;
  level: number;
  storageTypeId: string;
  isActive: boolean;
  storageType: any;
  properties: any;
  createdAt: string;
  updatedAt: string;
  aggregatedUniqueProducts: number;
  aggregatedTotalQuantity: number;
}

interface WarehouseProps {
  id: string;
  code: string;
  name: string;
  virtual: boolean;
  location: string;
  dimensions: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  stats: any;
  firstLevelStorageUnits: any;
}

const LS_KEY = 'on-hand-dynamic-prop-keys';

const OnHandListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<OnHand | null>(null);
  const [dynamicColumns, setDynamicColumns] = useState<PaginatedDataGridColumn[]>([]);
  const [showZeros, setShowZeros] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // ---------- helpers ----------
  const canonicalize = (k: string) => (k ?? '').toLowerCase().trim();

  const getCaseInsensitive = (obj: Record<string, any> | undefined, canonicalKey: string) => {
    if (!obj) return null;
    const exact = obj[canonicalKey];
    if (exact !== undefined) return exact;

    const found = Object.entries(obj).find(([kk]) => canonicalize(kk) === canonicalKey);
    return found ? found[1] : null;
  };

  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const specialCases: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season',
    };
    return specialCases[key] || formatted;
  }, []);

  // load previous "seen" for sorting only (not for rendering)
  const initialSeenKeys = (() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      const uniq = Array.isArray(arr) ? Array.from(new Set(arr.map((k: string) => canonicalize(k)))) : [];
      return uniq;
    } catch {
      return [];
    }
  })();

  const seenDynamicKeysRef = useRef<Set<string>>(new Set(initialSeenKeys));

  const persistSeenKeys = (set: Set<string>) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set)));
    } catch {}
  };

  // Field mapping for grouping/sorting/filtering - maps frontend field names to API field names
  const mapFieldForGrouping = useCallback((field: string): string => {
    const canonicalField = canonicalize(field);
    
    // Check if it's a dynamic property (from Unit.ItemAdditionalProperties)
    if (seenDynamicKeysRef.current.has(canonicalField)) {
      return `Unit.Item.json:${canonicalField}`;
    }
    
    // Map regular fields to their API equivalents for OnHand/InventoryItem
    const fieldMapping: Record<string, string> = {
      'unit.itemname': 'Unit.Item.Name',
      'unit.barcode': 'Unit.Barcode', 
      'unit.symbol': 'Unit.Symbol',
      'unit.unitofmeasurement': 'Unit.UnitOfMeasurement',
      'unit.issynced': 'Unit.IsSynced',
      // Direct OnHand fields
      'quantity': 'Quantity',
      'boxrefnumber': 'BoxRefNumber',
      // Warehouse fields (nested)
      'warehouse.name': 'Warehouse.Name',
      'warehouse.code': 'Warehouse.Code',
      // StorageUnit fields (nested)
      'storageunit.code': 'StorageUnit.Code',
      'storageunit.name': 'StorageUnit.Name',
      // Date fields
      'createdat': 'CreatedAt',
      'updatedat': 'UpdatedAt',
    };
    
    return fieldMapping[canonicalField] || field;
  }, []);

  // allow list of expected dynamic keys (canonicalized)
  const ALLOWED_DYNAMIC = new Set([
    'brand', 'color', 'size', 'category', 'maincategory', 'gender', 'season', 'generic'
  ]);

  // keep key only if it appears on >= 20% of current page rows (tweak as needed)
  const shouldKeepKey = (key: string, items: OnHand[]) => {
    let nonEmpty = 0;
    for (const it of items) {
      const v = getCaseInsensitive(it.unit?.itemAdditionalProperties, key);
      if (v !== null && v !== undefined && String(v).trim() !== '') nonEmpty++;
    }
    return nonEmpty >= Math.max(1, Math.floor(items.length * 0.2));
  };

  // ---------- column builders ----------
  const buildColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const base: PaginatedDataGridColumn[] = [
        {
          field: 'unit.barcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          groupable: false, // Barcode shouldn't be grouped
          valueGetter: (params) => params.row.unit?.barcode,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: 'pointer', fontWeight: 'medium' }}
              onClick={() => navigate(`/units/${params.row.unit?.id}`)}
            >
              {params.value || 'N/A'}
            </Typography>
          ),
        },
        {
          field: 'unit.itemName',
          headerName: 'Product Name',
          flex: 1.5,
          minWidth: 200,
          groupable: true, // Can group by product name
          valueGetter: (params) => params.row.unit?.itemName,
        },
        {
          field: 'unit.symbol',
          headerName: 'Symbol',
          flex: 1,
          minWidth: 120,
          groupable: true, // Can group by symbol
          valueGetter: (params) => params.row.unit?.symbol,
        },
        {
          field: 'quantity',
          headerName: 'Quantity',
          width: 100,
          type: 'number',
          groupable: true, // Can group by quantity ranges
          renderCell: (params: GridRenderCellParams) => (
            <Chip label={params.value ?? 0} color="primary" variant="outlined" size="small" />
          ),
        },
        {
          field: 'boxRefNumber',
          headerName: 'Box Reference',
          flex: 1,
          minWidth: 150,
          groupable: true, // Can group by box reference
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              sx={{
                cursor: params.value ? 'pointer' : 'default',
                '&:hover': { textDecoration: params.value ? 'underline' : 'none' },
              }}
              onClick={() => params.value && navigate(`/box/${params.value}`)}
            >
              {params.value || '-'}
            </Typography>
          ),
        },
        {
          field: 'warehouse.name',
          headerName: 'Warehouse',
          flex: 1,
          minWidth: 120,
          groupable: true, // Can group by warehouse
          valueGetter: (params) => params.row.warehouse?.name,
          renderCell: (params: GridRenderCellParams) => (
            <Chip label={params.value || 'N/A'} size="small" variant="outlined" color="secondary" />
          ),
        },
        {
          field: 'storageUnit.name',
          headerName: 'Storage Unit',
          flex: 1,
          minWidth: 130,
          groupable: true, // Can group by storage unit
          valueGetter: (params) => params.row.storageUnit?.name,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{
                cursor: params.row.storageUnit?.id ? 'pointer' : 'default',
                '&:hover': { textDecoration: params.row.storageUnit?.id ? 'underline' : 'none' },
              }}
              onClick={() =>
                params.row.storageUnit?.id && navigate(`/storage-unit/${params.row.storageUnit.id}`)
              }
            >
              {params.value || 'N/A'}
            </Typography>
          ),
        },
        {
          field: 'unit.unitOfMeasurement',
          headerName: 'Unit',
          width: 100,
          groupable: true, // Can group by unit of measurement
          valueGetter: (params) => params.row.unit?.unitOfMeasurement,
        },
        {
          field: 'unit.isSynced',
          headerName: 'Synced',
          width: 100,
          groupable: true, // Can group by sync status
          valueGetter: (params) => params.row.unit?.isSynced,
          renderCell: (params: GridRenderCellParams) => (
            <Chip
              label={params.value ? 'Yes' : 'No'}
              color={params.value ? 'success' : 'warning'}
              size="small"
              variant="outlined"
            />
          ),
        },
      ];

      const dynamicCols = dynamicKeys.sort().map((ck) => {
        const prettyHeader = formatPropertyName(ck);

        const col: PaginatedDataGridColumn = {
          field: ck,
          headerName: prettyHeader,
          flex: 1,
          minWidth: 110,
          valueGetter: (params) => getCaseInsensitive(params.row.unit?.itemAdditionalProperties, ck),
          sortable: true,
          groupable: true, // Additional properties can be grouped
        };

        if (ck.includes('color')) {
          col.columnType = 'color';
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            const colorValue = colorParser(params.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            );
          };
        }

        if (['category', 'maincategory', 'brand', 'season'].some((f) => ck.includes(f))) {
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            return <Chip label={params.value} size="small" variant="outlined" color="primary" />;
          };
        }

        return col;
      });

      const dateColumns: PaginatedDataGridColumn[] = [
        { field: 'createdAt', headerName: 'Added Date', width: 160, type: 'dateTime', groupable: false },
        { field: 'updatedAt', headerName: 'Last Updated', width: 160, type: 'dateTime', groupable: false },
      ];

      return [...base, ...dynamicCols, ...dateColumns];
    },
    [formatPropertyName, navigate]
  );

  // initialize with base+date only (no dynamic yet)
  React.useEffect(() => {
    setDynamicColumns(buildColumns([]));
  }, []);

  // ---------- data fetcher ----------
  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      const queryParams = new URLSearchParams({
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize),
      });

      if (params.search) queryParams.append('SearchTerm', params.search);
      if (showZeros) queryParams.append('includeZeros', 'true');
      
      // Handle grouping parameters
      const isGrouped = params.grouping && params.grouping.GroupBy.length > 0;
      
      if (isGrouped && params.grouping) {
        // Map grouping fields to API format
        params.grouping.GroupBy.forEach(field => {
          queryParams.append('GroupBy', mapFieldForGrouping(field));
        });
        queryParams.append('Level', String(params.grouping.Level));
        queryParams.append('GroupSortBy', params.grouping.GroupSortBy || 'key');
        queryParams.append('GroupSortDescending', String(params.grouping.GroupSortDescending || false));
        queryParams.append('IncludeCount', String(params.grouping.IncludeCount || true));
        
        // Add ParentKey if provided for drill-down
        if (params.grouping.ParentKey) {
          queryParams.append('ParentKey', params.grouping.ParentKey);
        }
      } else {
        // Handle regular sorting
        if (params.sortModel?.length > 0) {
          const { field, sort } = params.sortModel[0];
          const canonicalField = canonicalize(field);
          const isDynamic = seenDynamicKeysRef.current.has(canonicalField);

          let sortField = field;
          if (isDynamic) {
            sortField = `unit.itemAdditionalProperties.${canonicalField}`;
          } else if (field.startsWith('unit.') || field.startsWith('warehouse.') || field.startsWith('storageUnit.')) {
            sortField = field;
          }

          queryParams.append('SortBy', sortField);
          queryParams.append('SortDescending', String(sort === 'desc'));
        }
      }

      // Use grouped endpoint if grouping is active
      const endpoint = isGrouped ? 'api/InventoryItem/grouped' : 'api/InventoryItem';
      const res = await DataService.get(`${endpoint}?${queryParams.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to fetch inventory items`);
      const json = await res.json();

      let items: OnHand[] = [];
      let groups: any[] = [];
      
      if (isGrouped) {
        // Handle grouped response
        if (json?.data?.groups) {
          groups = Array.isArray(json.data.groups) ? json.data.groups : Object.values(json.data.groups);
        } else if (Array.isArray(json?.groups)) {
          groups = json.groups;
        }
      } else {
        // Handle regular response - normalize items
        if (json?.data?.items) {
          if (Array.isArray(json.data.items)) items = json.data.items;
          else if (typeof json.data.items === 'object') items = Object.values(json.data.items);
        } else if (Array.isArray(json?.items)) {
          items = json.items;
        }
      }

      // ---- collect present dynamic keys from THIS PAGE ONLY (only for regular items view) ----
      if (!isGrouped && items.length > 0) {
        const presentKeysSet = new Set<string>();

        for (const u of items) {
          const props = u.unit?.itemAdditionalProperties;
          if (!props || typeof props !== 'object') continue;

          for (const k of Object.keys(props)) {
            const ck = canonicalize(k);
            presentKeysSet.add(ck);
          }
        }

        // OPTIONAL: update 'seen' only for future case-insensitive sorting
        let dirtySeen = false;
        for (const ck of presentKeysSet) {
          if (!seenDynamicKeysRef.current.has(ck)) {
            seenDynamicKeysRef.current.add(ck);
            dirtySeen = true;
          }
        }
        if (dirtySeen) persistSeenKeys(seenDynamicKeysRef.current);

        // ---- filter noisy keys and rebuild columns from PRESENT keys only ----
        const presentKeys = Array.from(presentKeysSet)
          .filter((k) => ALLOWED_DYNAMIC.has(k))
          .filter((k) => shouldKeepKey(k, items))
          .sort();

        setDynamicColumns(buildColumns(presentKeys));
      }

      return {
        items: isGrouped ? undefined : items,
        groups: isGrouped ? groups : undefined,
        totalCount: json?.data?.totalCount ?? json?.totalCount ?? (isGrouped ? groups.length : items.length),
        totalGroups: isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : undefined,
        pageNumber: json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (isGrouped ? (json?.data?.totalGroups ?? json?.totalGroups ?? json?.data?.totalCount ?? json?.totalCount ?? groups.length) : (json?.data?.totalCount ?? json?.totalCount ?? items.length)) / params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false,
      };
    },
    [buildColumns, mapFieldForGrouping, showZeros]
  );

  // ---------- actions & handlers ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'toggle-zeros',
      title: showZeros ? 'Hide Zero Quantities' : 'Show Zero Quantities',
      icon: showZeros ? <FilterAltOffIcon /> : <FilterAltIcon />,
      onClick: () => { setShowZeros(p => !p); setRefreshKey(p => p + 1); },
    },
  ];

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await DataService.delete(`api/InventoryItem/delete/${deleteId}`, {});
      if (res.ok) {
        dispatch(showAlert({ message: 'Unit deleted successfully!', type: 'success' }));
        setRefreshKey((p) => p + 1);
      } else {
        dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
      }
    } catch {
      dispatch(showAlert({ message: 'Failed to delete Unit!', type: 'error' }));
    }
    setDeleteId(null);
  };

  const handleRowClick = (params: any) => navigate(`/units/${params.row.unit?.id}`);

  const handleEditUnit = (unit: OnHand) => {
    setSelectedUnit(unit);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = (_updatedUnit: any) => {
    dispatch(showAlert({ message: 'Unit updated successfully!', type: 'success' }));
    setRefreshKey((p) => p + 1);
    setEditDialogOpen(false);
    setSelectedUnit(null);
  };

  // ---------- render ----------
  return (
    <>
      <PaginatedDataGrid
        key={refreshKey}
        title="On Hand Inventory"
        columns={dynamicColumns}
        idField="id"
        useBackendPagination
        onDataFetch={fetchUnits}
        cacheTimeout={300000}
        actions={actions}
        enableGrouping={true}
        enableGroupDrillDown={true}
        fieldMapper={mapFieldForGrouping}
        wmsMode
        groupedUrl='api/InventoryItem/grouped/'
        densityLevel="standard"
        autoHeight={true}
        compactEmptyState={true}
        responsiveColumns={true}
        hideFooterWhenEmpty={true}
        striped
        maxHeight={700}
        onRowClick={handleRowClick}
        rowActions={[
          {
            key: 'edit',
            icon: <EditIcon />,
            label: 'Edit Unit',
            color: 'primary',
            onClick: (_id: GridRowId, row: any) => handleEditUnit(row),
          },
        ]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'background.default',
          },
        }}
      />

      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Unit"
        message="Are you sure you want to delete this Unit? This action cannot be undone."
      />

      <UnitDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUnit(null);
        }}
        unit={
          selectedUnit
            ? {
                ...selectedUnit,
                name: selectedUnit.unit.itemName,
                additionalProperties: selectedUnit.unit.itemAdditionalProperties,
                minNumber: selectedUnit.unit.itemMinNumber,
                maxNumber: selectedUnit.unit.itemMaxNumber,
                hasProperties:
                  !!selectedUnit.unit.itemAdditionalProperties &&
                  Object.keys(selectedUnit.unit.itemAdditionalProperties).length > 0,
                propertyCount: selectedUnit.unit.itemAdditionalProperties
                  ? Object.keys(selectedUnit.unit.itemAdditionalProperties).length
                  : 0,
                barcode: selectedUnit.unit.barcode,
                symbol: selectedUnit.unit.symbol,
                unitOfMeasurement: selectedUnit.unit.unitOfMeasurement,
                isSynced: selectedUnit.unit.isSynced,
                createdAt: selectedUnit.unit.createdAt,
                updatedAt: selectedUnit.unit.updatedAt,
              }
            : null
        }
        mode="edit"
        onSuccess={handleEditSuccess}
      />
    </>
  );
};

export default OnHandListing;
